import React from 'react';
import {
  Text,
  DetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  IDetailsListProps,
  DetailsRow,
  IColumn,
  useTheme,
} from '@fluentui/react';
import { Image, ImageFit } from '@fluentui/react/lib/Image';

interface IPlanillaDigitalOficialesDetailList {
  oficialesList: any[]; // todocambiar a interface
  addEventForOficial: (ppoId: number) => void;
  addEventForOficialAuxiliar: (ppoaId: number) => void;
}

const PlanillaDigitalOficialesDetailList = ({
  oficialesList,
  addEventForOficial,
  addEventForOficialAuxiliar,
}: IPlanillaDigitalOficialesDetailList) => {
  const [errorGoles, setErrorGoles] = React.useState(false);
  const [errorAmarillas, setErrorAmarillas] = React.useState(false);
  const [errorSuspensiones, setErrorSuspensiones] = React.useState(false);
  const [errorRojas, setErrorRojas] = React.useState(false);
  const [errorAzules, setErrorAzules] = React.useState(false);

  const oficialListColumns: IColumn[] = React.useMemo(
    () => [
      {
        key: 'N°',
        fieldName: 'tipo',
        name: 'N°',
        minWidth: 25,
        maxWidth: 25,
      },
      {
        key: 'Nombre',
        fieldName: 'nombre',
        name: 'Nombre',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        key: 'Goles',
        fieldName: 'goles',
        name: 'Goles',
        minWidth: 20,
        isIconOnly: true,
        onRender: (item) => {
          return <div style={{ textAlign: 'center' }}>{item['goles']}</div>;
        },
      },
      {
        key: 'Amarillas',
        fieldName: 'amarillas',
        name: 'Amarillas',
        minWidth: 20,
        onRenderHeader: (props) => {
          return (
            <span>
              {!errorAmarillas ? (
                <Image
                  src={'/img/tarjeta_amarilla.svg'}
                  onError={() => {
                    setErrorAmarillas(true);
                  }}
                  imageFit={ImageFit.cover}
                  styles={{
                    root: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                ></Image>
              ) : (
                <Text variant="smallPlus">Amarillas</Text>
              )}
            </span>
          );
        },
        onRender: (item) => {
          return (
            <div className={item.amarillas > 0 ? 'bg-amarilla' : ''} style={{ textAlign: 'center' }}>
              {item['amarillas'] > 0 ? item['amarillas'] : '-'}
            </div>
          );
        },
      },
      {
        key: 'Suspensiones',
        fieldName: 'suspensiones',
        name: 'Suspensiones',
        minWidth: 20,
        onRenderHeader: (props) => {
          return (
            <span>
              {!errorSuspensiones ? (
                <Image
                  src={'/img/dos_minutos.svg'}
                  onError={() => {
                    setErrorSuspensiones(true);
                  }}
                  imageFit={ImageFit.cover}
                  styles={{
                    root: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                ></Image>
              ) : (
                <Text variant="smallPlus">Suspensiones</Text>
              )}
            </span>
          );
        },
        onRender: (item) => {
          return (
            <div className={item.suspensiones > 0 ? 'bg-suspensiones' : ''} style={{ textAlign: 'center' }}>
              {item['suspensiones'] > 0 ? item['suspensiones'] : '-'}
            </div>
          );
        },
      },
      {
        key: 'Rojas',
        fieldName: 'rojas',
        name: 'Rojas',
        minWidth: 20,
        onRenderHeader: (props) => {
          return (
            <span>
              {!errorRojas ? (
                <Image
                  src={'/img/tarjeta_roja.svg'}
                  onError={() => {
                    setErrorRojas(true);
                  }}
                  imageFit={ImageFit.cover}
                  styles={{
                    root: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                ></Image>
              ) : (
                <Text variant="smallPlus">Rojas</Text>
              )}
            </span>
          );
        },
        onRender: (item) => {
          return (
            <div className={item.rojas > 0 ? 'bg-roja' : ''} style={{ textAlign: 'center' }}>
              {item['rojas'] > 0 ? item['rojas'] : '-'}
            </div>
          );
        },
      },
      {
        key: 'Azules',
        fieldName: 'azules',
        name: 'Azules',
        minWidth: 20,
        onRenderHeader: (props) => {
          return (
            <span>
              {!errorAzules ? (
                <Image
                  src={'/img/tarjeta_azul.svg'}
                  onError={() => {
                    setErrorAzules(true);
                  }}
                  imageFit={ImageFit.cover}
                  styles={{
                    root: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                ></Image>
              ) : (
                <Text variant="smallPlus">Azules</Text>
              )}
            </span>
          );
        },
        onRender: (item) => {
          return (
            <div className={item.azules > 0 ? 'bg-azules' : ''} style={{ textAlign: 'center' }}>
              {item['azules'] > 0 ? item['azules'] : '-'}
            </div>
          );
        },
      },
    ],
    [errorGoles, errorAmarillas, errorSuspensiones, errorRojas, errorAzules]
  );

  const getKey = (item: any, index?: number) => {
    return item.key;
  };

  const animationOnClick = (event: React.MouseEvent<HTMLElement>, item?: any): void => {
    const element = event.currentTarget;
    element.classList.add('blink-anim');

    element.addEventListener('animationend', () => animationEnd(element));
  };

  const animationEnd = (element: any) => {
    element.classList.remove('blink-anim');

    element.removeEventListener('animationend', animationEnd);
  };

  const theme = useTheme();
  const _onRenderOficialRow: IDetailsListProps['onRenderRow'] = (props) => {
    if (props) {
      return (
        <div
          onClick={(e) => {
            props.itemIndex === 0
              ? addEventForOficial(props.item.oficialId)
              : addEventForOficialAuxiliar(props.item.oficialId);
            animationOnClick(e);
          }}
        >
          <DetailsRow
            {...props}
            styles={{
              root: {
                backgroundColor: props.itemIndex % 2 === 0 ? theme.palette.neutralLight : 'transparent',
                selectors: {
                  ':hover': { backgroundColor: theme.palette.neutralQuaternaryAlt },
                },
              },
            }}
            className="planilla-detail-list"
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <DetailsList
        items={oficialesList}
        selectionMode={SelectionMode.none}
        columns={oficialListColumns}
        setKey="none"
        layoutMode={DetailsListLayoutMode.justified}
        isHeaderVisible={true}
        getKey={getKey}
        onRenderRow={_onRenderOficialRow}
        onShouldVirtualize={() => false}
      />
    </>
  );
};

export default PlanillaDigitalOficialesDetailList;
